import React from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import styles from './Footer.module.scss';
import {
  Instagram,
  Linkedin,
  Twitter,
  Mail,
  MapPin,
  Video
} from 'lucide-react';

const Footer = () => {
  const { t, i18n } = useTranslation('common');
  const location = useLocation();

  // Ocultar Footer en subrutas de Admin
  if (location.pathname.startsWith('/admin')) {
    return null;
  }

  // Animaciones del Design System
  const fadeInLuxury = {
    hidden: {
      opacity: 0,
      y: 30,
      filter: 'blur(10px)'
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: 'blur(0px)',
      transition: {
        duration: 0.8,
        ease: [0.23, 1, 0.32, 1]
      }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  const staggerItem = {
    hidden: {
      opacity: 0,
      y: 20,
      scale: 0.95
    },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: [0.23, 1, 0.32, 1]
      }
    }
  };

  const shimmerHover = {
    rest: {
      scale: 1,
      rotate: 0
    },
    hover: {
      scale: 1.1,
      rotate: 5,
      transition: {
        duration: 0.4,
        ease: [0.23, 1, 0.32, 1]
      }
    }
  };

  return (
    <motion.footer
      className={styles.footer}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.3 }}
      variants={fadeInLuxury}
    >
      <div className={styles.container}>
        <motion.div
          className={styles.content}
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Logo y Descripción */}
          <motion.div className={styles.brandSection} variants={staggerItem}>
            <h3 className={styles.logo}>FOTH</h3>
            <p className={styles.description}>
              {t('footer.description').split('. ')[0]}.<br />
              {t('footer.description').split('. ')[1]}
            </p>
          </motion.div>

          {/* Enlaces de Navegación */}
          <motion.div className={styles.column} variants={staggerItem}>
            <h4 className={styles.heading}>{t('footer.explore')}</h4>
            <ul className={styles.list}>
              <li>
                <Link to="/brands" className={styles.link}>{t('footer.brands')}</Link>
              </li>
              <li>
                <Link to="/creators" className={styles.link}>{t('footer.creators')}</Link>
              </li>
              <li>
                <Link to="/product" className={styles.link}>{t('footer.product')}</Link>
              </li>
            </ul>
          </motion.div>
          
          <motion.div className={styles.column} variants={staggerItem}>
            <h4 className={styles.heading}>{t('footer.company')}</h4>
            <ul className={styles.list}>
              <li>
                <Link to="/about" className={styles.link}>{t('footer.vision')}</Link>
              </li>
              <li>
                <Link to="/partners" className={styles.link}>{t('footer.partners')}</Link>
              </li>
              <li>
                <Link to="/help" className={styles.link}>{t('footer.help')}</Link>
              </li>
              <li>
                <Link to="/contact" className={styles.link}>{t('footer.contact')}</Link>
              </li>
            </ul>
          </motion.div>

          {/* Contacto & Social */}
          <motion.div className={styles.column} variants={staggerItem}>
            <h4 className={styles.heading}>{t('footer.learn_more')}</h4>
            <ul className={styles.list}>
              <li className={styles.contactItem}>
                <Mail size={16} />
                <a href="mailto:admin@wearefoth.com">admin@wearefoth.com</a>
              </li>
              <li className={styles.contactItem}>
                <MapPin size={16} />
                <span>{t('footer.location')}</span>
              </li>
            </ul>

            <div className={styles.socialLinks} style={{ marginTop: '16px' }}>
              <motion.a href="https://instagram.com/wearefoth" target="_blank" rel="noopener noreferrer" className={styles.socialLink} variants={shimmerHover} whileHover="hover">
                <Instagram size={18} />
              </motion.a>
              <motion.a href="https://linkedin.com/company/wearefoth" target="_blank" rel="noopener noreferrer" className={styles.socialLink} variants={shimmerHover} whileHover="hover">
                <Linkedin size={18} />
              </motion.a>
              <motion.a href="https://tiktok.com/@wearefoth" target="_blank" rel="noopener noreferrer" className={styles.socialLink} variants={shimmerHover} whileHover="hover">
                <Video size={18} />
              </motion.a>
              <motion.a href="https://twitter.com/wearefoth" target="_blank" rel="noopener noreferrer" className={styles.socialLink} variants={shimmerHover} whileHover="hover">
                <Twitter size={18} />
              </motion.a>
            </div>
          </motion.div>
        </motion.div>

        {/* Separador */}
        <motion.div
          className={styles.divider}
          variants={staggerItem}
        ></motion.div>

        {/* Copyright */}
        <motion.div
          className={styles.bottom}
          variants={staggerContainer}
        >
          <motion.div className={styles.copyright} variants={staggerItem}>
            <p>&copy; {new Date().getFullYear()} FOTH. {t('footer.rights')}</p>
            <p className={styles.signature}>
              {t('footer.signature')}
            </p>
          </motion.div>

          <div className={styles.bottomActions}>
            <div className={styles.langSwitcher}>
              {['es', 'en', 'pt'].map((lang) => (
                <button
                  key={lang}
                  onClick={() => i18n.changeLanguage(lang)}
                  className={clsx(
                    styles.langItem,
                    i18n.language.split('-')[0] === lang && styles.activeLang
                  )}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>

            <ul className={styles.legal}>
              <li><Link to="/privacy-policy">{t('footer.privacy')}</Link></li>
              <li><Link to="/terms">{t('footer.terms')}</Link></li>
              <li><Link to="/data-deletion">{t('footer.data_deletion')}</Link></li>
              <li><Link to="/cookies">{t('footer.cookies')}</Link></li>
            </ul>
          </div>
        </motion.div>
      </div>
    </motion.footer>
  );
};

export default Footer;
